/**
 * Header of the Schedule Detail modal — title, status pills and the
 * header-level actions (hold, close Manual-Agreement remainder, OTIF override).
 *
 * Extracted from ``ScheduleDetailModal.tsx`` so that file stays under the
 * project's 500-line ceiling. Purely presentational: every decision about
 * WHO may act is made by the parent and passed in as a flag.
 */
import { X, PauseCircle, ShieldCheck, CircleSlash } from 'lucide-react';
import type { POLineSchedule } from '../../lib/api';
import { mayReleaseHold, viewerSideOf } from './lineStatus';
import ManualAgreementBadge from './ManualAgreementBadge';

/** Writeback status display config. */
const WB_BADGE: Record<string, { label: string; cls: string }> = {
  accepted: { label: 'WB: Accepted', cls: 'bg-green-100 text-green-800' },
  declined: { label: 'WB: Declined', cls: 'bg-red-100 text-red-800' },
  change_odd: { label: 'WB: Change ODD', cls: 'bg-orange-100 text-orange-800' },
  change_crd: { label: 'WB: Change CRD', cls: 'bg-orange-100 text-orange-800' },
  accepted_unit_price: { label: 'WB: Price Accepted', cls: 'bg-blue-100 text-blue-800' },
  accepted_qty: { label: 'WB: Qty Accepted', cls: 'bg-blue-100 text-blue-800' },
};

interface ScheduleDetailHeaderProps {
  schedule: POLineSchedule | null;
  user: { company_type?: string | null; platform?: string | null } | null | undefined;
  ackBadge: { label: string; cls: string; title?: string };
  /** Staff-only OTIF override control. */
  canOtifOverride: boolean;
  /** Customer-side close of a manual-agreement remainder. */
  canCloseHeld: boolean;
  onClose: () => void;
  onPutOnHold: () => void;
  onToggleHold: () => void;
  onCloseHeld: () => void;
  onOtifOverride: () => void;
}

export default function ScheduleDetailHeader({
  schedule, user, ackBadge, canOtifOverride, canCloseHeld,
  onClose, onPutOnHold, onToggleHold, onCloseHeld, onOtifOverride,
}: ScheduleDetailHeaderProps) {
  return (
    <div className="flex items-center justify-between p-5 border-b bg-muted/30">
      <div className="flex items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-primary">
            Schedule: {schedule?.po_number} / Line {schedule?.line_number} / Sched {schedule?.schedule_line_number || '-'}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Part: <span className="font-mono font-medium">{schedule?.part_number}</span>
            {schedule && <span className="ml-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ackBadge.cls}`} title={ackBadge.title}>{ackBadge.label}</span>
            </span>}
            {schedule?.wb_status && WB_BADGE[schedule.wb_status] && (
              <span className="ml-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${WB_BADGE[schedule.wb_status].cls}`}>
                  {WB_BADGE[schedule.wb_status].label}
                </span>
              </span>
            )}
            {schedule && schedule.status !== 'cancelled' && schedule.status !== 'shipped' && (
              <ManualAgreementBadge schedule={schedule} className="ml-2" />
            )}
            {schedule?.otif_overridden && (
              <span className="ml-2">
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"
                  title={schedule.otif_override_reason ? `Manual override: ${schedule.otif_override_reason}` : 'Manual OTIF override'}
                >
                  <ShieldCheck className="h-3 w-3" /> OTIF Passed (Override)
                </span>
              </span>
            )}
          </p>
        </div>
        {schedule && (() => {
          // Who may release is ONE rule (lineStatus.mayReleaseHold), shared
          // with both grids and the line modal and mirroring the server's
          // release_hold. This copy keyed off the PLACER, so it knew nothing
          // about G7's nominee and offered Release to a side that had handed
          // the hold over (S1, gate round 1).
          const canRelease = mayReleaseHold(viewerSideOf(user), schedule);
          if (schedule.on_hold && !canRelease) {
            return (
              <span
                className="px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 bg-red-100 text-red-700 cursor-not-allowed"
                title={ackBadge.title}
              >
                <PauseCircle className="h-4 w-4" />
                {ackBadge.label}
              </span>
            );
          }
          return (
            <button
              onClick={() => {
                if (schedule.on_hold) onToggleHold();
                else onPutOnHold();
              }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${
                schedule.on_hold
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={schedule.on_hold ? 'Click to release from hold' : 'Click to put on hold'}
            >
              <PauseCircle className="h-4 w-4" />
              {schedule.on_hold ? 'Take Off Hold' : 'Put On Hold'}
            </button>
          );
        })()}
        {/* Close the remainder of a manually agreed line — customer side only.
            The auto-close reconcile skips held slots, so this is the only
            way the line ever closes when the ERP never catches up. */}
        {schedule && canCloseHeld && schedule.collab_hold
          && schedule.status !== 'cancelled' && schedule.status !== 'shipped' && (
          <button
            onClick={onCloseHeld}
            className="px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors bg-violet-700 text-white hover:bg-violet-800"
            title="Close the open remainder of this manually agreed line; receipts stay on it"
            data-testid="close-held-btn"
          >
            <CircleSlash className="h-4 w-4" />
            Close Line
          </button>
        )}
        {/* Staff-only OTIF override — hidden for line-0 anchors and
            dateless rows (they carry no OTIF verdict; backend rejects). */}
        {schedule && canOtifOverride && schedule.schedule_line_number !== '0' && schedule.schedule_date && (
          <button
            onClick={onOtifOverride}
            className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${
              schedule.otif_overridden
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            title={schedule.otif_overridden ? 'View / remove the manual OTIF override' : 'Mark this schedule OTIF-passed with a reason (staff only)'}
          >
            <ShieldCheck className="h-4 w-4" />
            {schedule.otif_overridden ? 'OTIF Overridden' : 'OTIF Override'}
          </button>
        )}
      </div>
      <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}
